import React from 'react';
import { ShieldCheck, Clock, XCircle, AlertTriangle, Star, Crown } from 'lucide-react';

export const VerificationBadge = ({ status, size = 'default' }) => {
  const config = {
    verified: {
      label: 'Verified',
      icon: ShieldCheck,
      className: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
    },
    pending: {
      label: 'Pending',
      icon: Clock,
      className: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
    },
    rejected: {
      label: 'Rejected',
      icon: XCircle,
      className: 'bg-red-500/15 text-red-400 border-red-500/30',
    },
    suspended: {
      label: 'Suspended',
      icon: AlertTriangle,
      className: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
    },
  };

  const { label, icon: Icon, className } = config[status] || config.pending;

  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5 gap-1',
    default: 'text-xs px-2 py-0.5 gap-1',
    lg: 'text-sm px-2.5 py-1 gap-1.5',
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    default: 'w-3 h-3',
    lg: 'w-4 h-4',
  };

  return (
    <span
      className={`inline-flex items-center rounded font-medium border ${className} ${sizeClasses[size]}`}
      data-testid={`verification-badge-${status}`}
    >
      <Icon className={iconSizes[size]} />
      {label}
    </span>
  );
};

export const RoleBadge = ({ role }) => {
  const roleLabels = {
    carrier: 'Carrier',
    owner_operator: 'Owner Operator',
    fleet: 'Fleet',
    dispatcher: 'Dispatcher',
    broker: 'Broker',
    shipper: 'Shipper',
    dealer: 'Dealer',
    auction: 'Auction',
    rental_fleet_owner: 'Rental/Fleet',
    admin: 'Admin',
  };

  const label = roleLabels[role] || role;

  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary text-muted-foreground"
      data-testid={`role-badge-${role}`}
    >
      {label}
    </span>
  );
};

export const LoadStatusBadge = ({ status }) => {
  const config = {
    posted: { label: 'Posted', className: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
    requested: { label: 'Requested', className: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
    accepted: { label: 'Accepted', className: 'bg-sky-600/15 text-sky-400 border-sky-600/30' },
    assigned: { label: 'Assigned', className: 'bg-blue-700/15 text-blue-300 border-blue-700/30' },
    picked_up: { label: 'Picked Up', className: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30' },
    delivered: { label: 'Delivered', className: 'bg-green-500/15 text-green-400 border-green-500/30' },
    completed: { label: 'Completed', className: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
    cancelled: { label: 'Cancelled', className: 'bg-red-500/15 text-red-400 border-red-500/30' },
  };

  const { label, className } = config[status] || { label: status, className: 'bg-secondary text-muted-foreground' };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${className}`}
      data-testid={`status-badge-${status}`}
    >
      {label}
    </span>
  );
};

export const RequestStatusBadge = ({ status }) => {
  const config = {
    pending: { label: 'Pending', className: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
    approved: { label: 'Approved', className: 'bg-green-500/15 text-green-400 border-green-500/30' },
    declined: { label: 'Declined', className: 'bg-red-500/15 text-red-400 border-red-500/30' },
    withdrawn: { label: 'Withdrawn', className: 'bg-gray-500/15 text-gray-400 border-gray-500/30' },
  };

  const { label, className } = config[status] || config.pending;

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${className}`}
      data-testid={`request-status-badge-${status}`}
    >
      {label}
    </span>
  );
};

export const CategoryBadge = ({ category }) => {
  const categoryLabels = {
    fraud_alerts: 'Fraud Alerts',
    dispatch_help: 'Dispatch Help',
    carrier_lounge: 'Carrier Lounge',
    broker_talk: 'Broker Talk',
    industry_news: 'Industry News',
    auto_transport: 'Auto Transport',
    hotshot: 'Hotshot',
    freight: 'Freight',
    haullogix_updates: 'HaulLogix Updates',
  };

  const categoryColors = {
    fraud_alerts: 'bg-red-500/15 text-red-400',
    dispatch_help: 'bg-blue-500/15 text-blue-400',
    carrier_lounge: 'bg-green-500/15 text-green-400',
    broker_talk: 'bg-purple-500/15 text-purple-400',
    industry_news: 'bg-cyan-500/15 text-cyan-400',
    auto_transport: 'bg-orange-500/15 text-orange-400',
    hotshot: 'bg-yellow-500/15 text-yellow-400',
    freight: 'bg-indigo-500/15 text-indigo-400',
    haullogix_updates: 'bg-orange-500/15 text-orange-400',
  };

  const label = categoryLabels[category] || category;
  const colorClass = categoryColors[category] || 'bg-secondary text-muted-foreground';

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}
      data-testid={`category-badge-${category}`}
    >
      {label}
    </span>
  );
};

// Rating Badge - shows average rating with star
export const RatingBadge = ({ rating, count, size = 'default' }) => {
  if (!rating || rating === 0) return null;
  
  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    default: 'text-xs px-2 py-0.5',
    lg: 'text-sm px-2.5 py-1',
  };
  
  const starSizes = {
    sm: 'w-3 h-3',
    default: 'w-3.5 h-3.5',
    lg: 'w-4 h-4',
  };
  
  return (
    <span
      className={`inline-flex items-center gap-1 rounded font-medium bg-[#4ADE80]/15 text-[#4ADE80] ${sizeClasses[size]}`}
      data-testid="rating-badge"
    >
      <Star className={`${starSizes[size]} fill-[#4ADE80]`} />
      <span>{rating.toFixed(1)}</span>
      {count !== undefined && (
        <span className="text-[#4ADE80]/70">({count})</span>
      )}
    </span>
  );
};

// Founding Member Badge
export const FoundingMemberBadge = ({ tier, size = 'default' }) => {
  if (!tier) return null;
  
  const config = {
    lifetime: {
      label: 'Lifetime Founder',
      className: 'bg-gradient-to-r from-[#4ADE80]/20 to-[#16A34A]/20 text-[#86EFAC] border-[#4ADE80]/40',
    },
    three_year: {
      label: '3-Year Founder',
      className: 'bg-[#4ADE80]/15 text-[#4ADE80] border-[#4ADE80]/30',
    },
    one_year: {
      label: '1-Year Founder',
      className: 'bg-[#16A34A]/15 text-[#B8860B] border-[#16A34A]/30',
    },
  };
  
  const { label, className } = config[tier] || {};
  if (!label) return null;
  
  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5 gap-1',
    default: 'text-xs px-2 py-0.5 gap-1',
    lg: 'text-sm px-2.5 py-1 gap-1.5',
  };
  
  const iconSizes = {
    sm: 'w-3 h-3',
    default: 'w-3.5 h-3.5',
    lg: 'w-4 h-4',
  };
  
  return (
    <span
      className={`inline-flex items-center rounded font-medium border ${className} ${sizeClasses[size]}`}
      data-testid={`founding-badge-${tier}`}
    >
      <Crown className={iconSizes[size]} />
      {label}
    </span>
  );
};
